// src/components/layout/AdminBreadcrumb.tsx
import { Link } from "react-router-dom";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { ChevronRight, Home } from "lucide-react";
import { Fragment } from "react";

export interface AdminBreadcrumbSegment {
  label: string;
  href?: string;
  state?: Record<string, any>;
}

interface AdminBreadcrumbProps {
  segments: AdminBreadcrumbSegment[];
}

/**
 * Breadcrumb navigation for admin role.
 * Extends from Admin Console through the full hierarchy:
 * Admin Console > Clients > {Client} > {SubOrg} > {Portfolio} > {Assessment}
 */
export function AdminBreadcrumb({ segments }: AdminBreadcrumbProps) {
  // Always prepend the Admin Console root
  const fullSegments: AdminBreadcrumbSegment[] = [
    { label: "Admin Console", href: "/admin" },
    ...segments,
  ];

  return (
    <Breadcrumb className="mb-4">
      <BreadcrumbList>
        {fullSegments.map((segment, index) => {
          const isLast = index === fullSegments.length - 1;
          const isFirst = index === 0;

          return (
            <Fragment key={index}>
              <BreadcrumbItem>
                {isLast || !segment.href ? (
                  <BreadcrumbPage className="flex items-center gap-1">
                    {isFirst && <Home className="h-3.5 w-3.5" />}
                    {segment.label}
                  </BreadcrumbPage>
                ) : (
                  <BreadcrumbLink asChild>
                    <Link 
                      to={segment.href} 
                      state={segment.state}
                      className="flex items-center gap-1"
                    >
                      {isFirst && <Home className="h-3.5 w-3.5" />}
                      {segment.label}
                    </Link>
                  </BreadcrumbLink>
                )}
              </BreadcrumbItem>
              {!isLast && (
                <BreadcrumbSeparator>
                  <ChevronRight className="h-4 w-4" />
                </BreadcrumbSeparator>
              )}
            </Fragment>
          );
        })}
      </BreadcrumbList>
    </Breadcrumb>
  );
}
